import * as React from 'react';
// Import hooks.
import {
	useInputForm,
} from '@app/hooks/view/input';
import {
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import services.
import {
	StringService,
} from '@app/services';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';
// Import props.
import {
	InputFormProps,
	InputTextWithoutInputTextBoxProps,
} from '@app/props';

interface UseInputEmailParameters {
	id: string;
	label: string;
	infoText?: string;
	required: boolean;
	disabled: boolean;
	value: string;
	showValidation?: boolean;
	validators?: ((input: string, required: boolean) => ValidatorResponse)[];
}

type UseInputEmailResults = UseInputFormResult<string, InputTextWithoutInputTextBoxProps>

const useInputEmail = (params: UseInputEmailParameters): UseInputEmailResults => {

	// #region exported methods

	/**
	 * Returns props for form input. 
	 **/
	const applyEmailInputProps = (): InputTextWithoutInputTextBoxProps => {
		// First apply the form input props.
		let formProps: InputFormProps = applyProps();
		// Then apply the email input props.
		return {
			...formProps,
			infoText: params.infoText,
			onChange: other.setInput,
			value: other.value,
		}
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Validates the current value.
	 * @param value
	 * @param required
	 */
	const validateEmail_UndefinedChecks = (value: string, required: boolean): ValidatorResponse => {
		let response: ValidatorResponse = new ValidatorResponse(true, String.empty());

		if (required
			&& String.isNullOrWhiteSpace(value)) {
			response = new ValidatorResponse(false, 'Email is required.');
		}

		return response;
	}

	/**
	 * Validates the current value.
	 * @param value
	 * @param required
	 */
	const validateEmail_Format = (value: string, required: boolean): ValidatorResponse => {
		let response: ValidatorResponse = new ValidatorResponse(true, String.empty());

		if (!String.isNullOrWhiteSpace(value)
			&& !StringService.getInstance().validateEmailString(value)) {
			response = new ValidatorResponse(false, 'The provided email is invalid.');
		}

		return response;
	}

	// #endregion private methods


	// #region variables

	const validators = React.useMemo(() => params.validators ?? [], [params.validators]);

	// Instantiate the 'email' input.
	const { applyProps, ...other } = useInputForm<string>({
		id: params.id,
		label: params.label,
		required: params.required,
		disabled: params.disabled,
		initialInput: params.value,
		showValidation: params.showValidation,
		validators: [validateEmail_UndefinedChecks, validateEmail_Format, ...validators],
	});

	// #endregion variables


	return {
		applyProps: applyEmailInputProps,
		...other,
	};
}

// Export parameters.
export {
	UseInputEmailParameters,
}

// Export results.
export {
	UseInputEmailResults,
}

export default useInputEmail;